import { useState, type FormEvent } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from '@mlm/ui/components/toast';
import type { CoachingActionUpdate } from '@mlm/shared';
import { addCalendarDays } from '@mlm/shared/coaching';
import { cn } from '@mlm/ui/lib/cn';
import {
  Block,
  CoachButton,
  EmptyCircle,
  ErrorNote,
  Field,
  inputClass,
  Loading,
  SageCheck,
  Screen,
} from '../components/primitives';
import { coaching } from '../lib/api';
import { shortDay } from '../lib/format';
import { qk, useActiveGoals, useRefreshCoaching, useToday } from '../lib/queries';

/** SC-35: complete, partly complete, skip, move to tomorrow, or add an action. */
export function ActionsPage() {
  const today = useToday();
  const goals = useActiveGoals();
  const refresh = useRefreshCoaching();
  const [tab, setTab] = useState<'today' | 'tomorrow'>('today');
  const [title, setTitle] = useState('');
  const [minutes, setMinutes] = useState('');
  const [priority, setPriority] = useState(2);
  const [goalId, setGoalId] = useState('');

  const day = today.data?.day ?? '';
  const target = day ? (tab === 'today' ? day : addCalendarDays(day, 1)) : '';
  const list = useQuery({
    queryKey: qk.actions(target, target),
    queryFn: () => coaching.actions({ day: target }),
    enabled: Boolean(target),
  });

  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: CoachingActionUpdate }) =>
      coaching.updateAction(id, patch),
    onSuccess: (a) => {
      if (a.status === 'done') toast.success('Done. One step closer.');
      void refresh();
    },
  });
  const remove = useMutation({
    mutationFn: coaching.deleteAction,
    onSuccess: () => void refresh(),
  });
  const create = useMutation({
    mutationFn: () =>
      coaching.createAction({
        title: title.trim(),
        priority,
        estimatedMinutes: minutes ? Math.max(1, Math.min(600, Number(minutes))) : null,
        goalId: goalId || null,
        scheduledDate: target,
      }),
    meta: { successMessage: 'Action added.' },
    onSuccess: () => {
      setTitle('');
      setMinutes('');
      void refresh();
    },
  });

  if (today.isPending) return <Loading />;
  if (today.isError) return <ErrorNote error={today.error} retry={() => void today.refetch()} />;
  const actions = list.data?.actions ?? [];
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (title.trim().length >= 2) create.mutate();
  };

  return (
    <Screen
      back="/achieve"
      eyebrow={shortDay(target || day)}
      title={tab === 'today' ? "Today's actions" : "Tomorrow's actions"}
      subtitle="Three focused actions can move your goals forward."
    >
      <div className="flex gap-6 border-b border-border px-5" role="tablist">
        {(['today', 'tomorrow'] as const).map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === t}
            onClick={() => setTab(t)}
            className={cn(
              'border-b-2 py-3 font-mono text-[0.66rem] uppercase tracking-[0.14em]',
              tab === t ? 'border-verde text-ink' : 'border-transparent text-sage hover:text-ink',
            )}
          >
            {t}
          </button>
        ))}
      </div>

      {list.isPending && <Loading />}
      {!list.isPending && actions.length === 0 && (
        <Block title="You're clear for this day.">
          Add one meaningful action below, or ask your coach tomorrow.
        </Block>
      )}
      {actions.map((a) => (
        <div key={a.id} className="border-b border-border px-5 py-4">
          <div className="flex items-start gap-3.5">
            <button
              type="button"
              className="mt-0.5 shrink-0"
              aria-label={
                a.status === 'done' ? `Mark "${a.title}" as not done` : `Mark "${a.title}" as done`
              }
              onClick={() =>
                update.mutate({
                  id: a.id,
                  patch: { status: a.status === 'done' ? 'pending' : 'done' },
                })
              }
            >
              {a.status === 'done' ? <SageCheck /> : <EmptyCircle />}
            </button>
            <div className="min-w-0 flex-1">
              <p
                className={cn(
                  'text-[0.95rem] leading-snug',
                  a.status === 'done' ? 'text-ink/60 line-through' : 'text-ink',
                )}
              >
                {a.title}
              </p>
              <p className="eyebrow mt-1">
                {[
                  `Priority ${a.priority}`,
                  a.estimatedMinutes ? `${a.estimatedMinutes} min` : null,
                  a.status === 'partial' ? 'Partly done' : null,
                  a.status === 'skipped' ? 'Skipped' : null,
                  a.rescheduledFrom ? 'Rescheduled' : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
              <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2">
                {a.status === 'pending' && (
                  <CoachButton
                    kind="quiet"
                    onClick={() => update.mutate({ id: a.id, patch: { status: 'partial' } })}
                  >
                    Partly done
                  </CoachButton>
                )}
                {tab === 'today' && a.status !== 'done' && (
                  <CoachButton
                    kind="quiet"
                    onClick={() =>
                      update.mutate({ id: a.id, patch: { scheduledDate: addCalendarDays(day, 1) } })
                    }
                  >
                    Move to tomorrow
                  </CoachButton>
                )}
                {a.status === 'pending' && (
                  <CoachButton
                    kind="quiet"
                    onClick={() => update.mutate({ id: a.id, patch: { status: 'skipped' } })}
                  >
                    Skip
                  </CoachButton>
                )}
                <CoachButton kind="quiet" onClick={() => remove.mutate(a.id)}>
                  Delete
                </CoachButton>
              </div>
            </div>
          </div>
        </div>
      ))}

      <form onSubmit={onSubmit} className="space-y-4 px-5 py-6">
        <Field label="Add an action" htmlFor="action-title">
          <input
            id="action-title"
            className={inputClass}
            placeholder="e.g. Contact three prospects"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Priority" htmlFor="action-priority">
            <select
              id="action-priority"
              className={inputClass}
              value={priority}
              onChange={(e) => setPriority(Number(e.target.value))}
            >
              <option value={1}>1 (most important)</option>
              <option value={2}>2</option>
              <option value={3}>3</option>
            </select>
          </Field>
          <Field label="Minutes" htmlFor="action-minutes">
            <input
              id="action-minutes"
              type="number"
              min={1}
              max={600}
              className={inputClass}
              value={minutes}
              onChange={(e) => setMinutes(e.target.value)}
            />
          </Field>
        </div>
        {(goals.data?.length ?? 0) > 0 && (
          <Field label="Goal" htmlFor="action-goal">
            <select
              id="action-goal"
              className={inputClass}
              value={goalId}
              onChange={(e) => setGoalId(e.target.value)}
            >
              <option value="">No goal</option>
              {goals.data?.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.title}
                </option>
              ))}
            </select>
          </Field>
        )}
        <CoachButton type="submit" disabled={title.trim().length < 2 || create.isPending}>
          Add action
        </CoachButton>
      </form>
    </Screen>
  );
}
